import { createFileRoute, Link, ErrorComponent, notFound } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listPublishedPosts, getBlogPageContent } from "@/lib/blog.functions";
import BlogHeader from "@/components/blog/BlogHeader";

const postsQuery = queryOptions({
  queryKey: ["blog", "posts"],
  queryFn: () => listPublishedPosts(),
});
const contentQuery = queryOptions({
  queryKey: ["blog", "content"],
  queryFn: () => getBlogPageContent(),
});

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: "Blog — Backland Luxury Nature Resort" },
      {
        name: "description",
        content:
          "Stories, guides, and inspiration from Backland — a luxury nature resort in Northern Arizona.",
      },
      { property: "og:title", content: "Backland Blog" },
      {
        property: "og:description",
        content: "Stories, guides, and inspiration from Northern Arizona.",
      },
      { property: "og:url", content: "https://www.travelbackland.com/blog" },
    ],
    links: [{ rel: "canonical", href: "https://www.travelbackland.com/blog" }],
  }),
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(postsQuery),
      context.queryClient.ensureQueryData(contentQuery),
    ]),
  errorComponent: ErrorComponent,
  notFoundComponent: () => <div>Not found</div>,
  component: BlogIndex,
});

function formatDate(iso: string | null) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return "";
  }
}

function BlogIndex() {
  const { data: posts } = useSuspenseQuery(postsQuery);
  const { data: content } = useSuspenseQuery(contentQuery);

  return (
    <div
      className="fixed inset-0 overflow-y-auto bg-background text-foreground"
      style={{ touchAction: "auto", overscrollBehavior: "auto" }}
    >
      <BlogHeader fallbackBack={null} />
      <header className="border-b border-border">
        <div className="max-w-5xl mx-auto px-6 pb-12 pt-6">
          <h1 className="text-4xl md:text-5xl font-serif">{content.hero_title}</h1>
          {content.hero_subtitle && (
            <p className="mt-3 text-base md:text-lg text-muted-foreground max-w-2xl">
              {content.hero_subtitle}
            </p>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        {posts.length === 0 ? (
          <p className="text-muted-foreground">No posts yet — check back soon.</p>
        ) : (
          <ul className="grid gap-10 md:grid-cols-2">
            {posts.map((p) => (
              <li key={p.id}>
                <Link
                  to="/blog/$slug"
                  params={{ slug: p.slug }}
                  className="group block"
                >
                  {p.cover_image_url && (
                    <div className="aspect-[16/10] overflow-hidden rounded-lg bg-muted">
                      <img
                        src={p.cover_image_url}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                      />
                    </div>
                  )}
                  <div className="mt-4">
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">
                      {formatDate(p.published_at)}
                      {p.minutes_to_read ? ` · ${p.minutes_to_read} min read` : ""}
                    </div>
                    <h2 className="mt-2 text-xl md:text-2xl font-serif group-hover:underline">
                      {p.title}
                    </h2>
                    {p.excerpt && (
                      <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{p.excerpt}</p>
                    )}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
